import {clearableTextKeys, clearableTextValuesAreEqual, StockItem} from '../../../shared-utilities/models-old/datastructures';
import {isEqual} from 'lodash';
import {
  IStockManagerBarcodeConflict,
  IStockManagerBarcodeValidationRequest,
} from '../models/stock-manager-barcode-duplicate.model';
import {
  getMultiplePriceChangeInclusiveFields,
} from '../../../shared/shared-utils/price/multiple-price-change.utils';

export const FIRESTORE_BARCODE_QUERY_BATCH_SIZE = 30;

export const BARCODE_CHECK_LOADING_THRESHOLD = 1000;

/**
 * Fields whose values must be compared numerically, as the grid and Firestore can hold either
 * a number or its string equivalent.
 */
const NUMERIC_COMPARISON_FIELDS: string[] = [
  'sellPriIncl1',
  'nominalGP',
  'recommendedGP',
  'latestCost',
  ...getMultiplePriceChangeInclusiveFields(),
];

export function normalizeBarcodeValue(value: string | number | null | undefined): string {
  return String(value ?? '').trim();
}

function normalizeToStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  return value
    .map((entry: string | number) => normalizeBarcodeValue(entry))
    .filter((entry: string) => entry.length > 0);
}

export function normalizeMultipleBarcodesValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((entry: string | number) => normalizeBarcodeValue(entry))
      .filter((entry: string) => entry.length > 0);
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    return value
      .split(',')
      .map((entry: string) => normalizeBarcodeValue(entry))
      .filter((entry: string) => entry.length > 0);
  }
  return [];
}

export function multipleBarcodesValuesAreEqual(firstValue: unknown, secondValue: unknown): boolean {
  const firstBarcodes = normalizeMultipleBarcodesValue(firstValue);
  const secondBarcodes = normalizeMultipleBarcodesValue(secondValue);
  if (firstBarcodes.length !== secondBarcodes.length) {
    return false;
  }
  return firstBarcodes.every((barcode: string, index: number) => barcode === secondBarcodes[index]);
}

export function formatMultipleBarcodesForDisplay(value: unknown): string {
  return normalizeMultipleBarcodesValue(value).join(',');
}

export function multipleSuppliersValuesAreEqual(firstValue: unknown, secondValue: unknown): boolean {
  const firstSuppliers = normalizeMultipleSuppliersValue(firstValue);
  const secondSuppliers = normalizeMultipleSuppliersValue(secondValue);
  if (firstSuppliers.length !== secondSuppliers.length) {
    return false;
  }
  const sortedFirst = [...firstSuppliers].sort();
  const sortedSecond = [...secondSuppliers].sort();
  return sortedFirst.every((supplier: string, index: number) => supplier === sortedSecond[index]);
}

export function normalizeMultipleSuppliersValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((entry: string | number) => String(entry).trim())
      .filter((entry: string) => entry.length > 0);
  }
  return [];
}

export function stockManagerFieldValuesAreEqual(field: string, firstValue: unknown, secondValue: unknown): boolean {
  if (field === 'multipleBarcodes') {
    return multipleBarcodesValuesAreEqual(firstValue, secondValue);
  }
  if (field === 'multipleSuppliers') {
    return multipleSuppliersValuesAreEqual(firstValue, secondValue);
  }
  if (field === '_tags') {
    return tagArrayValuesAreEqual(firstValue, secondValue);
  }
  if (clearableTextKeys.includes(field)) {
    return clearableTextValuesAreEqual(firstValue, secondValue);
  }
  if (field === 'regularSuppCode' || field === 'suppCode') {
    return String(firstValue ?? '').trim() === String(secondValue ?? '').trim();
  }
  if (NUMERIC_COMPARISON_FIELDS.includes(field)) {
    return numericValuesAreEqual(firstValue, secondValue);
  }
  return firstValue === secondValue;
}

/**
 * Normalises stock tag values from arrays or comma-separated strings into one string per tag.
 */
export function normalizeStockItemTagValues(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.flatMap((item: unknown) => normalizeStockItemTagValues(item));
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    return raw
      .split(',')
      .map((item: string) => item.trim())
      .filter((item: string) => item !== '');
  }
  return [];
}

/**
 * Compares numeric stock values. Two values that are both missing or non-numeric count as equal.
 */
function numericValuesAreEqual(firstValue: unknown, secondValue: unknown): boolean {
  const first = Number(firstValue);
  const second = Number(secondValue);
  if (Number.isNaN(first) && Number.isNaN(second)) {
    return true;
  }
  return first === second;
}

function tagArrayValuesAreEqual(firstValue: unknown, secondValue: unknown): boolean {
  const first = Array.isArray(firstValue) ? firstValue.map(String) : [];
  const second = Array.isArray(secondValue) ? secondValue.map(String) : [];
  if (first.length !== second.length) {
    return false;
  }
  const sortedFirst = [...first].sort();
  const sortedSecond = [...second].sort();
  return sortedFirst.every((tag: string, index: number) => tag === sortedSecond[index]);
}

export function collectBarcodesForStockItem(stockItem: StockItem): string[] {
  const primary = normalizeBarcodeValue(stockItem.barcode);
  const additional = (Array.isArray(stockItem.multipleBarcodes) ? stockItem.multipleBarcodes : [])
    .map((barcode: string | number) => normalizeBarcodeValue(barcode))
    .filter((barcode: string) => barcode.length > 0);

  const uniqueBarcodes = new Set<string>();
  if (primary.length > 0) {
    uniqueBarcodes.add(primary);
  }
  additional.forEach((barcode: string) => uniqueBarcodes.add(barcode));
  return Array.from(uniqueBarcodes);
}

export function stockItemHasBarcodeFieldChanges(stockItem: StockItem): boolean {
  const original = stockItem.originalValue;
  if (!original) {
    return false;
  }

  const currentBarcodeSnapshot = collectBarcodesForStockItem(stockItem).sort().join('|');
  const originalSnapshotItem = {
    ...stockItem,
    barcode: original.barcode?.value as string | number,
    multipleBarcodes: normalizeToStringArray(original.multipleBarcodes?.value),
  } as StockItem;

  const originalBarcodeSnapshot = collectBarcodesForStockItem(originalSnapshotItem).sort().join('|');
  return !isEqual(currentBarcodeSnapshot, originalBarcodeSnapshot);
}

export function countUniqueBarcodesToValidate(validationRequests: IStockManagerBarcodeValidationRequest[]): number {
  const uniqueBarcodes = new Set<string>();
  validationRequests.forEach((request: IStockManagerBarcodeValidationRequest) => {
    request.barcodes.forEach((barcode: string) => {
      const normalized = normalizeBarcodeValue(barcode);
      if (normalized.length > 0) {
        uniqueBarcodes.add(normalized);
      }
    });
  });
  return uniqueBarcodes.size;
}

export function buildBarcodeValidationRequests(editedItems: StockItem[]): IStockManagerBarcodeValidationRequest[] {
  return editedItems
    .filter((item: StockItem) => stockItemHasBarcodeFieldChanges(item))
    .map((item: StockItem) => ({
      stockCode: String(item.code ?? '').trim(),
      barcodes: collectBarcodesForStockItem(item),
    }))
    .filter((request: IStockManagerBarcodeValidationRequest) => request.stockCode.length > 0 && request.barcodes.length > 0);
}

export function findPendingEditBarcodeConflicts(
  validationRequests: IStockManagerBarcodeValidationRequest[],
  stockCodeToDescription: Record<string, string>,
): IStockManagerBarcodeConflict[] {
  const barcodeOwners = new Map<string, string[]>();
  const conflicts: IStockManagerBarcodeConflict[] = [];

  validationRequests.forEach((request: IStockManagerBarcodeValidationRequest) => {
    request.barcodes.forEach((barcode: string) => {
      const owners = barcodeOwners.get(barcode) ?? [];
      if (!owners.includes(request.stockCode)) {
        owners.push(request.stockCode);
      }
      barcodeOwners.set(barcode, owners);
    });
  });

  barcodeOwners.forEach((owners: string[], barcode: string) => {
    if (owners.length < 2) {
      return;
    }
    const editingStockCode = owners[0];
    owners.slice(1).forEach((existingStockCode: string) => {
      conflicts.push({
        barcode,
        editingStockCode,
        existingStockCode,
        existingStockDescription: stockCodeToDescription[existingStockCode] ?? 'No description',
      });
    });
  });

  return conflicts;
}

export function chunkBarcodes(barcodes: string[], chunkSize: number = FIRESTORE_BARCODE_QUERY_BATCH_SIZE): string[][] {
  const chunks: string[][] = [];
  for (let index = 0; index < barcodes.length; index += chunkSize) {
    chunks.push(barcodes.slice(index, index + chunkSize));
  }
  return chunks;
}
